import { QueryClient } from "react-query";

/**
 * The app-wide React Query client.
 *
 * Lives in its own module (rather than inline in App.jsx) so that non-hook code
 * — e.g. the Planning module's imperative load effects — can reach the SAME
 * cache instance via `queryClient.fetchQuery` / `invalidateQueries` that the
 * `<QueryClientProvider>` and every `useQuery` hook use. One client, one cache.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 4xx responses are permission/auth/validation errors — retrying them
      // multiplies error noise (e.g. parabot endpoints firing 3x on a 403)
      // without ever succeeding. Only retry on 5xx / network errors.
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (typeof status === "number" && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
      // Treat fetched data as fresh for 60s. react-query's default staleTime is
      // 0 (stale immediately), which means every screen refetches on every mount
      // and every navigation. 60s makes navigating away and back an instant
      // cache hit while keeping financial data current enough — and mutations
      // that call invalidateQueries still force an immediate refetch regardless.
      staleTime: 60 * 1000,
      // Keep unused query data around for 15 min so back-navigation rehydrates
      // from cache instantly before any background refetch resolves.
      cacheTime: 15 * 60 * 1000,
      // Don't refetch on every window refocus (v3 defaults this to true). Lots of
      // components already opt out per-query; this just makes it the default.
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Invalidate a query after a mutation, forcing a fresh refetch even if a
 * fetch for the same key is already in flight.
 *
 * Why this exists: react-query v3's invalidateQueries dedupes onto an
 * in-flight fetch by default, so a post-mutation invalidation can resolve
 * with PRE-mutation data and pin the stale result until reload. Passing
 * `{ cancelRefetch: true }` (third argument in the key-first overload)
 * cancels the in-flight fetch and starts a new one.
 *
 * Use this for all post-mutation invalidations (e.g. in onSuccess handlers).
 */
export const invalidateAfterMutation = (queryKey, filters) =>
  queryClient.invalidateQueries(queryKey, filters, { cancelRefetch: true });

export default queryClient;
